/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface SpecialtyTeamMemberProps {
  id: string;
  specialtyTeamId: string;
  teamMemberId: string;
  weightInKg: number | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class SpecialtyTeamMemberRecord extends Record<SpecialtyTeamMemberProps>({
  id: null as any,
  specialtyTeamId: null as any,
  teamMemberId: null as any,
  weightInKg: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    specialtyTeamId: string;
    teamMemberId: string;
    weightInKg: number | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): SpecialtyTeamMemberRecord {
    if (json == null) {
      throw new Error('SpecialtyTeamMemberRecord.fromJS: parameter cannot be null');
    }
    return new SpecialtyTeamMemberRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'SpecialtyTeamMemberJson.id',
      ),
      specialtyTeamId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'specialtyTeamId', '00000000-0000-0000-0000-000000000000'),
        'SpecialtyTeamMemberJson.specialtyTeamId',
      ),
      teamMemberId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'teamMemberId', '00000000-0000-0000-0000-000000000000'),
        'SpecialtyTeamMemberJson.teamMemberId',
      ),
      weightInKg: json.weightInKg,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'SpecialtyTeamMemberJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    specialtyTeamId: string;
    teamMemberId: string;
    weightInKg: number | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [SpecialtyTeamMemberRecord, Error[]] {
    if (json == null) {
      throw new Error('SpecialtyTeamMemberRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new SpecialtyTeamMemberRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'SpecialtyTeamMemberJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      specialtyTeamId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'specialtyTeamId', '00000000-0000-0000-0000-000000000000'),
            'SpecialtyTeamMemberJson.specialtyTeamId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      teamMemberId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'teamMemberId', '00000000-0000-0000-0000-000000000000'),
            'SpecialtyTeamMemberJson.teamMemberId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      weightInKg: tryOrDefault(
        () => json.weightInKg,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'SpecialtyTeamMemberJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: SpecialtyTeamMemberProps) {
    super(props);
    if (props == null) {
      throw new Error('SpecialtyTeamMemberRecord constructor parameter cannot be null');
    }
  }
}

export abstract class SpecialtyTeamMemberViewModelBase implements SpecialtyTeamMemberProps {
  protected readonly _props: SpecialtyTeamMemberProps;
  private _json?: SpecialtyTeamMemberProps;

  get id(): string {
    return this._props.id;
  }
  get specialtyTeamId(): string {
    return this._props.specialtyTeamId;
  }
  get teamMemberId(): string {
    return this._props.teamMemberId;
  }
  get weightInKg(): number | null {
    return this._props.weightInKg;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: SpecialtyTeamMemberProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        specialtyTeamId: this._props.specialtyTeamId,
        teamMemberId: this._props.teamMemberId,
        weightInKg: this._props.weightInKg,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllSpecialtyTeamMemberEntities = (state: AppState) =>
  state.entityStores.specialtyTeamMember.entities;

export const selectSpecialtyTeamMemberById = (id: string) => (state: AppState) =>
  state.entityStores.specialtyTeamMember.entities.get(id);

export const selectSpecialtyTeamMemberMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.specialtyTeamMember.meta.get(id);

export const selectSpecialtyTeamMemberAndMetadataById = (id: string) =>
  createSelector(
    selectSpecialtyTeamMemberById(id),
    selectSpecialtyTeamMemberMetadataById(id),
    (specialtyTeamMember, meta) => ({ specialtyTeamMember, meta }),
  );

export function selectSpecialtyTeamMemberEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllSpecialtyTeamMemberEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, SpecialtyTeamMemberRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectSpecialtyTeamMemberEntitiesBySpecialtyTeamId(specialtyTeamId: string) {
  return createSelector(selectAllSpecialtyTeamMemberEntities, (entities) =>
    entities.filter((e) => e.specialtyTeamId === specialtyTeamId).valueSeq(),
  );
}

export function selectSpecialtyTeamMemberEntitiesByTeamMemberId(teamMemberId: string) {
  return createSelector(selectAllSpecialtyTeamMemberEntities, (entities) =>
    entities.filter((e) => e.teamMemberId === teamMemberId).valueSeq(),
  );
}

export function selectSpecialtyTeamMemberEntitiesByWeightInKg(weightInKg: number) {
  return createSelector(selectAllSpecialtyTeamMemberEntities, (entities) =>
    entities.filter((e) => e.weightInKg === weightInKg).valueSeq(),
  );
}

export function selectSpecialtyTeamMemberEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllSpecialtyTeamMemberEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
